package com.kanati.admin.features.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.kanati.admin.components.ActionButton
import com.kanati.admin.components.FieldRow
import com.kanati.admin.components.TextField
import com.kanati.admin.state.MaterialThemePalette
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonArray
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.intOrNull
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flex
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.margin
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Text

private val guidedEditorJson = Json {
    prettyPrint = true
    explicitNulls = false
}

internal sealed interface AppDocumentEditorAvailability {
    data class Ready(val draft: NativeUiDocumentDraft) : AppDocumentEditorAvailability
    data class Blank(val suggestion: NativeUiDocumentDraft) : AppDocumentEditorAvailability
    data class RawFallback(val message: String) : AppDocumentEditorAvailability
}

internal data class NativeUiDocumentDraft(
    val id: String,
    val version: String,
    val rootPageId: String,
    val pages: List<NativeUiPageDraft>,
)

internal data class NativeUiPageDraft(
    val id: String,
    val slug: String = "",
    val title: String = "",
    val blocks: List<NativeUiBlockDraft> = emptyList(),
)

internal sealed interface NativeUiBlockDraft {
    val id: String
    val type: NativeUiBlockType
}

internal data class NativeUiTextBlockDraft(
    override val id: String = "",
    val text: String = "",
    val variant: NativeUiTextVariant = NativeUiTextVariant.Body,
    val colorRole: NativeUiColorRoleDraft? = null,
    override val type: NativeUiBlockType = NativeUiBlockType.Text,
) : NativeUiBlockDraft

internal data class NativeUiImageBlockDraft(
    override val id: String = "",
    val url: String = "",
    val contentDescription: String = "",
    val aspectRatio: String = "",
    override val type: NativeUiBlockType = NativeUiBlockType.Image,
) : NativeUiBlockDraft

internal data class NativeUiButtonBlockDraft(
    override val id: String = "",
    val label: String = "",
    val action: NativeUiActionDraft = NativeUiActionDraft(),
    val style: NativeUiButtonStyleDraft = NativeUiButtonStyleDraft.Primary,
    val containerColorRole: NativeUiColorRoleDraft? = null,
    val contentColorRole: NativeUiColorRoleDraft? = null,
    val borderColorRole: NativeUiColorRoleDraft? = null,
    override val type: NativeUiBlockType = NativeUiBlockType.Button,
) : NativeUiBlockDraft

internal data class NativeUiSpacerBlockDraft(
    override val id: String = "",
    val heightDp: Int = 24,
    override val type: NativeUiBlockType = NativeUiBlockType.Spacer,
) : NativeUiBlockDraft

internal data class NativeUiSectionBlockDraft(
    override val id: String = "",
    val title: String = "",
    val blocks: List<NativeUiBlockDraft> = emptyList(),
    override val type: NativeUiBlockType = NativeUiBlockType.Section,
) : NativeUiBlockDraft

internal data class NativeUiCardBlockDraft(
    override val id: String = "",
    val title: String = "",
    val body: String = "",
    val action: NativeUiActionDraft = NativeUiActionDraft(),
    val blocks: List<NativeUiBlockDraft> = emptyList(),
    val style: NativeUiCardStyleDraft = NativeUiCardStyleDraft.Standard,
    val containerColorRole: NativeUiColorRoleDraft? = null,
    val contentColorRole: NativeUiColorRoleDraft? = null,
    val borderColorRole: NativeUiColorRoleDraft? = null,
    override val type: NativeUiBlockType = NativeUiBlockType.Card,
) : NativeUiBlockDraft

internal data class NativeUiCarouselBlockDraft(
    override val id: String = "",
    val items: List<NativeUiCarouselItemDraft> = emptyList(),
    override val type: NativeUiBlockType = NativeUiBlockType.Carousel,
) : NativeUiBlockDraft

internal data class NativeUiCarouselItemDraft(
    val imageUrl: String = "",
    val title: String = "",
    val subtitle: String = "",
    val contentDescription: String = "",
    val action: NativeUiActionDraft = NativeUiActionDraft(),
)

internal data class NativeUiListBlockDraft(
    override val id: String = "",
    val items: List<NativeUiListItemDraft> = emptyList(),
    override val type: NativeUiBlockType = NativeUiBlockType.List,
) : NativeUiBlockDraft

internal data class NativeUiListItemDraft(
    val title: String = "",
    val subtitle: String = "",
    val overline: String = "",
    val action: NativeUiActionDraft = NativeUiActionDraft(),
)

internal data class NativeUiEventCardBlockDraft(
    override val id: String = "",
    val title: String = "",
    val subtitle: String = "",
    val dateLabel: String = "",
    val locationLabel: String = "",
    val imageUrl: String = "",
    val action: NativeUiActionDraft = NativeUiActionDraft(),
    override val type: NativeUiBlockType = NativeUiBlockType.EventCard,
) : NativeUiBlockDraft

internal data class NativeUiTicketCardBlockDraft(
    override val id: String = "",
    val title: String = "",
    val subtitle: String = "",
    val status: String = "",
    val holderName: String = "",
    val qrLabel: String = "",
    val action: NativeUiActionDraft = NativeUiActionDraft(),
    override val type: NativeUiBlockType = NativeUiBlockType.TicketCard,
) : NativeUiBlockDraft

internal enum class NativeUiBlockType(val wireValue: String, val label: String) {
    Text("text", "Text"),
    Image("image", "Image"),
    Button("button", "Button"),
    Spacer("spacer", "Spacer"),
    Section("section", "Section"),
    Card("card", "Card"),
    Carousel("carousel", "Carousel"),
    List("list", "List"),
    EventCard("eventCard", "Event Card"),
    TicketCard("ticketCard", "Ticket Card"),
}

internal enum class NativeUiTextVariant(val wireValue: String, val label: String) {
    Title("Title", "Title"),
    Body("Body", "Body"),
    Label("Label", "Label"),
}

internal enum class NativeUiButtonStyleDraft(val wireValue: String, val label: String) {
    Primary("Primary", "Primary"),
    Secondary("Secondary", "Secondary"),
    Text("Text", "Text"),
}

internal enum class NativeUiCardStyleDraft(val wireValue: String, val label: String) {
    Standard("Standard", "Standard"),
    Featured("Featured", "Featured"),
}

internal enum class NativeUiColorRoleDraft(val wireValue: String, val label: String) {
    Primary("Primary", "Primary"),
    OnPrimary("OnPrimary", "On Primary"),
    Secondary("Secondary", "Secondary"),
    OnSecondary("OnSecondary", "On Secondary"),
    Background("Background", "Background"),
    OnBackground("OnBackground", "On Background"),
    Surface("Surface", "Surface"),
    OnSurface("OnSurface", "On Surface"),
    SurfaceVariant("SurfaceVariant", "Surface Variant"),
    OnSurfaceVariant("OnSurfaceVariant", "On Surface Variant"),
    Outline("Outline", "Outline"),
    OutlineVariant("OutlineVariant", "Outline Variant"),
    SurfaceTint("SurfaceTint", "Surface Tint"),
    Error("Error", "Error"),
    OnError("OnError", "On Error"),
}

internal enum class NativeUiActionType(val label: String) {
    None("None"),
    Page("Page"),
    Url("URL"),
    Refresh("Refresh"),
    Custom("Custom"),
}

internal data class NativeUiActionDraft(
    val type: NativeUiActionType = NativeUiActionType.None,
    val pageId: String = "",
    val slug: String = "",
    val url: String = "",
    val actionId: String = "",
    val payload: String = "",
)

private data class EditorOutlineEntry(
    val pathKey: String,
    val depth: Int,
    val eyebrow: String,
    val title: String,
    val meta: String,
    val breadcrumb: String,
)

internal fun resolveAppDocumentEditorAvailability(
    rawJson: String,
    requiredTabRouteIds: List<String>,
    homeTabId: String,
): AppDocumentEditorAvailability {
    val candidate = rawJson.trim()
    if (candidate.isBlank()) {
        return AppDocumentEditorAvailability.Blank(
            suggestion = createSuggestedDocumentDraft(requiredTabRouteIds, homeTabId)
        )
    }
    return runCatching {
        AppDocumentEditorAvailability.Ready(parseDocumentDraft(candidate))
    }.getOrElse { exception ->
        AppDocumentEditorAvailability.RawFallback(
            message = exception.message ?: "This app document uses fields the guided editor does not support yet."
        )
    }
}

internal fun encodeDocumentDraft(draft: NativeUiDocumentDraft): String =
    guidedEditorJson.encodeToString(
        JsonElement.serializer(),
        buildJsonObject {
            put("id", JsonPrimitive(draft.id))
            put("version", JsonPrimitive(draft.version))
            put("rootPageId", JsonPrimitive(draft.rootPageId))
            put(
                "pages",
                JsonArray(draft.pages.map(::pageToJson))
            )
        },
    )

private fun pageToJson(page: NativeUiPageDraft): JsonObject =
    buildJsonObject {
        put("id", JsonPrimitive(page.id))
        if (page.slug.isNotBlank()) put("slug", JsonPrimitive(page.slug))
        if (page.title.isNotBlank()) put("title", JsonPrimitive(page.title))
        put("blocks", JsonArray(page.blocks.map(::blockToJson)))
    }

private fun blockToJson(block: NativeUiBlockDraft): JsonObject =
    when (block) {
        is NativeUiTextBlockDraft -> buildJsonObject {
            put("type", JsonPrimitive(block.type.wireValue))
            put("text", JsonPrimitive(block.text))
            put("variant", JsonPrimitive(block.variant.wireValue))
            block.id.ifNotBlank { put("id", JsonPrimitive(it)) }
            block.colorRole?.let { put("colorRole", JsonPrimitive(it.wireValue)) }
        }

        is NativeUiImageBlockDraft -> buildJsonObject {
            put("type", JsonPrimitive(block.type.wireValue))
            put("url", JsonPrimitive(block.url))
            block.id.ifNotBlank { put("id", JsonPrimitive(it)) }
            block.contentDescription.ifNotBlank { put("contentDescription", JsonPrimitive(it)) }
            block.aspectRatio.toFloatOrNull()?.let { put("aspectRatio", JsonPrimitive(it)) }
        }

        is NativeUiButtonBlockDraft -> buildJsonObject {
            put("type", JsonPrimitive(block.type.wireValue))
            put("label", JsonPrimitive(block.label))
            put("style", JsonPrimitive(block.style.wireValue))
            block.id.ifNotBlank { put("id", JsonPrimitive(it)) }
            actionToJson(block.action)?.let { put("action", it) }
            block.containerColorRole?.let { put("containerColorRole", JsonPrimitive(it.wireValue)) }
            block.contentColorRole?.let { put("contentColorRole", JsonPrimitive(it.wireValue)) }
            block.borderColorRole?.let { put("borderColorRole", JsonPrimitive(it.wireValue)) }
        }

        is NativeUiSpacerBlockDraft -> buildJsonObject {
            put("type", JsonPrimitive(block.type.wireValue))
            put("heightDp", JsonPrimitive(block.heightDp))
            block.id.ifNotBlank { put("id", JsonPrimitive(it)) }
        }

        is NativeUiSectionBlockDraft -> buildJsonObject {
            put("type", JsonPrimitive(block.type.wireValue))
            block.id.ifNotBlank { put("id", JsonPrimitive(it)) }
            block.title.ifNotBlank { put("title", JsonPrimitive(it)) }
            put("blocks", JsonArray(block.blocks.map(::blockToJson)))
        }

        is NativeUiCardBlockDraft -> buildJsonObject {
            put("type", JsonPrimitive(block.type.wireValue))
            put("style", JsonPrimitive(block.style.wireValue))
            block.id.ifNotBlank { put("id", JsonPrimitive(it)) }
            block.title.ifNotBlank { put("title", JsonPrimitive(it)) }
            block.body.ifNotBlank { put("body", JsonPrimitive(it)) }
            actionToJson(block.action)?.let { put("action", it) }
            if (block.blocks.isNotEmpty()) put("blocks", JsonArray(block.blocks.map(::blockToJson)))
            block.containerColorRole?.let { put("containerColorRole", JsonPrimitive(it.wireValue)) }
            block.contentColorRole?.let { put("contentColorRole", JsonPrimitive(it.wireValue)) }
            block.borderColorRole?.let { put("borderColorRole", JsonPrimitive(it.wireValue)) }
        }

        is NativeUiCarouselBlockDraft -> buildJsonObject {
            put("type", JsonPrimitive(block.type.wireValue))
            block.id.ifNotBlank { put("id", JsonPrimitive(it)) }
            put(
                "items",
                buildJsonArray {
                    block.items.forEach { item ->
                        add(
                            buildJsonObject {
                                put("imageUrl", JsonPrimitive(item.imageUrl))
                                item.title.ifNotBlank { put("title", JsonPrimitive(it)) }
                                item.subtitle.ifNotBlank { put("subtitle", JsonPrimitive(it)) }
                                item.contentDescription.ifNotBlank { put("contentDescription", JsonPrimitive(it)) }
                                actionToJson(item.action)?.let { put("action", it) }
                            }
                        )
                    }
                },
            )
        }

        is NativeUiListBlockDraft -> buildJsonObject {
            put("type", JsonPrimitive(block.type.wireValue))
            block.id.ifNotBlank { put("id", JsonPrimitive(it)) }
            put(
                "items",
                buildJsonArray {
                    block.items.forEach { item ->
                        add(
                            buildJsonObject {
                                put("title", JsonPrimitive(item.title))
                                item.subtitle.ifNotBlank { put("subtitle", JsonPrimitive(it)) }
                                item.overline.ifNotBlank { put("overline", JsonPrimitive(it)) }
                                actionToJson(item.action)?.let { put("action", it) }
                            }
                        )
                    }
                },
            )
        }

        is NativeUiEventCardBlockDraft -> buildJsonObject {
            put("type", JsonPrimitive(block.type.wireValue))
            put("title", JsonPrimitive(block.title))
            block.id.ifNotBlank { put("id", JsonPrimitive(it)) }
            block.subtitle.ifNotBlank { put("subtitle", JsonPrimitive(it)) }
            block.dateLabel.ifNotBlank { put("dateLabel", JsonPrimitive(it)) }
            block.locationLabel.ifNotBlank { put("locationLabel", JsonPrimitive(it)) }
            block.imageUrl.ifNotBlank { put("imageUrl", JsonPrimitive(it)) }
            actionToJson(block.action)?.let { put("action", it) }
        }

        is NativeUiTicketCardBlockDraft -> buildJsonObject {
            put("type", JsonPrimitive(block.type.wireValue))
            put("title", JsonPrimitive(block.title))
            block.id.ifNotBlank { put("id", JsonPrimitive(it)) }
            block.subtitle.ifNotBlank { put("subtitle", JsonPrimitive(it)) }
            block.status.ifNotBlank { put("status", JsonPrimitive(it)) }
            block.holderName.ifNotBlank { put("holderName", JsonPrimitive(it)) }
            block.qrLabel.ifNotBlank { put("qrLabel", JsonPrimitive(it)) }
            actionToJson(block.action)?.let { put("action", it) }
        }
    }

private fun actionToJson(action: NativeUiActionDraft): JsonObject? =
    when (action.type) {
        NativeUiActionType.None -> null
        NativeUiActionType.Page -> buildJsonObject {
            put("type", JsonPrimitive("page"))
            action.pageId.ifNotBlank { put("pageId", JsonPrimitive(it)) }
            action.slug.ifNotBlank { put("slug", JsonPrimitive(it)) }
        }

        NativeUiActionType.Url -> buildJsonObject {
            put("type", JsonPrimitive("url"))
            put("url", JsonPrimitive(action.url))
        }

        NativeUiActionType.Refresh -> buildJsonObject {
            put("type", JsonPrimitive("refresh"))
        }

        NativeUiActionType.Custom -> buildJsonObject {
            put("type", JsonPrimitive("custom"))
            put("actionId", JsonPrimitive(action.actionId))
            action.payload.ifNotBlank { put("payload", JsonPrimitive(it)) }
        }
    }

private fun parseDocumentDraft(rawJson: String): NativeUiDocumentDraft {
    val root = guidedEditorJson.parseToJsonElement(rawJson).jsonObject
    ensureOnlyKeys(root, setOf("id", "version", "rootPageId", "pages"), "Document")
    val pages = root.requiredArray("pages", "Document").mapIndexed { index, element ->
        parsePageDraft(element.jsonObject, "Document page[$index]")
    }
    return NativeUiDocumentDraft(
        id = root.requiredString("id", "Document"),
        version = root.requiredString("version", "Document"),
        rootPageId = root.requiredString("rootPageId", "Document"),
        pages = pages,
    )
}

private fun parsePageDraft(page: JsonObject, path: String): NativeUiPageDraft {
    ensureOnlyKeys(page, setOf("id", "slug", "title", "blocks"), path)
    return NativeUiPageDraft(
        id = page.requiredString("id", path),
        slug = page.optionalString("slug"),
        title = page.optionalString("title"),
        blocks = page.requiredArray("blocks", path).mapIndexed { index, element ->
            parseBlockDraft(element.jsonObject, "$path block[$index]")
        },
    )
}

private fun parseBlockDraft(block: JsonObject, path: String): NativeUiBlockDraft {
    val type = block.requiredString("type", path)
    return when (NativeUiBlockType.entries.firstOrNull { it.wireValue == type }) {
        NativeUiBlockType.Text -> {
            ensureOnlyKeys(block, setOf("type", "id", "text", "variant", "colorRole"), path)
            NativeUiTextBlockDraft(
                id = block.optionalString("id"),
                text = block.requiredString("text", path),
                variant = parseEnum(
                    value = block.optionalString("variant").ifBlank { NativeUiTextVariant.Body.wireValue },
                    options = NativeUiTextVariant.entries,
                    path = "$path variant",
                ),
                colorRole = block.optionalEnum("colorRole", NativeUiColorRoleDraft.entries, "$path colorRole"),
            )
        }

        NativeUiBlockType.Image -> {
            ensureOnlyKeys(block, setOf("type", "id", "url", "contentDescription", "aspectRatio"), path)
            NativeUiImageBlockDraft(
                id = block.optionalString("id"),
                url = block.requiredString("url", path),
                contentDescription = block.optionalString("contentDescription"),
                aspectRatio = block["aspectRatio"]?.jsonPrimitive?.contentOrNull.orEmpty(),
            )
        }

        NativeUiBlockType.Button -> {
            ensureOnlyKeys(
                block,
                setOf("type", "id", "label", "action", "style", "containerColorRole", "contentColorRole", "borderColorRole"),
                path,
            )
            NativeUiButtonBlockDraft(
                id = block.optionalString("id"),
                label = block.requiredString("label", path),
                action = block.optionalAction("$path action"),
                style = parseEnum(
                    value = block.optionalString("style").ifBlank { NativeUiButtonStyleDraft.Primary.wireValue },
                    options = NativeUiButtonStyleDraft.entries,
                    path = "$path style",
                ),
                containerColorRole = block.optionalEnum("containerColorRole", NativeUiColorRoleDraft.entries, "$path containerColorRole"),
                contentColorRole = block.optionalEnum("contentColorRole", NativeUiColorRoleDraft.entries, "$path contentColorRole"),
                borderColorRole = block.optionalEnum("borderColorRole", NativeUiColorRoleDraft.entries, "$path borderColorRole"),
            )
        }

        NativeUiBlockType.Spacer -> {
            ensureOnlyKeys(block, setOf("type", "id", "heightDp"), path)
            NativeUiSpacerBlockDraft(
                id = block.optionalString("id"),
                heightDp = block["heightDp"]?.jsonPrimitive?.intOrNull
                    ?: error("$path heightDp must be an integer"),
            )
        }

        NativeUiBlockType.Section -> {
            ensureOnlyKeys(block, setOf("type", "id", "title", "blocks"), path)
            NativeUiSectionBlockDraft(
                id = block.optionalString("id"),
                title = block.optionalString("title"),
                blocks = block.requiredArray("blocks", path).mapIndexed { index, element ->
                    parseBlockDraft(element.jsonObject, "$path nested block[$index]")
                },
            )
        }

        NativeUiBlockType.Card -> {
            ensureOnlyKeys(
                block,
                setOf("type", "id", "title", "body", "action", "blocks", "style", "containerColorRole", "contentColorRole", "borderColorRole"),
                path,
            )
            NativeUiCardBlockDraft(
                id = block.optionalString("id"),
                title = block.optionalString("title"),
                body = block.optionalString("body"),
                action = block.optionalAction("$path action"),
                blocks = block["blocks"]?.jsonArray?.mapIndexed { index, element ->
                    parseBlockDraft(element.jsonObject, "$path nested block[$index]")
                }.orEmpty(),
                style = parseEnum(
                    value = block.optionalString("style").ifBlank { NativeUiCardStyleDraft.Standard.wireValue },
                    options = NativeUiCardStyleDraft.entries,
                    path = "$path style",
                ),
                containerColorRole = block.optionalEnum("containerColorRole", NativeUiColorRoleDraft.entries, "$path containerColorRole"),
                contentColorRole = block.optionalEnum("contentColorRole", NativeUiColorRoleDraft.entries, "$path contentColorRole"),
                borderColorRole = block.optionalEnum("borderColorRole", NativeUiColorRoleDraft.entries, "$path borderColorRole"),
            )
        }

        NativeUiBlockType.Carousel -> {
            ensureOnlyKeys(block, setOf("type", "id", "items"), path)
            val items = block.requiredArray("items", path)
            if (items.isEmpty()) {
                error("$path carousel must contain at least one item.")
            }
            NativeUiCarouselBlockDraft(
                id = block.optionalString("id"),
                items = items.mapIndexed { index, element ->
                    parseCarouselItem(element.jsonObject, "$path item[$index]")
                },
            )
        }

        NativeUiBlockType.List -> {
            ensureOnlyKeys(block, setOf("type", "id", "items"), path)
            NativeUiListBlockDraft(
                id = block.optionalString("id"),
                items = block.requiredArray("items", path).mapIndexed { index, element ->
                    parseListItem(element.jsonObject, "$path item[$index]")
                },
            )
        }

        NativeUiBlockType.EventCard -> {
            ensureOnlyKeys(block, setOf("type", "id", "title", "subtitle", "dateLabel", "locationLabel", "imageUrl", "action"), path)
            NativeUiEventCardBlockDraft(
                id = block.optionalString("id"),
                title = block.requiredString("title", path),
                subtitle = block.optionalString("subtitle"),
                dateLabel = block.optionalString("dateLabel"),
                locationLabel = block.optionalString("locationLabel"),
                imageUrl = block.optionalString("imageUrl"),
                action = block.optionalAction("$path action"),
            )
        }

        NativeUiBlockType.TicketCard -> {
            ensureOnlyKeys(block, setOf("type", "id", "title", "subtitle", "status", "holderName", "qrLabel", "action"), path)
            NativeUiTicketCardBlockDraft(
                id = block.optionalString("id"),
                title = block.requiredString("title", path),
                subtitle = block.optionalString("subtitle"),
                status = block.optionalString("status"),
                holderName = block.optionalString("holderName"),
                qrLabel = block.optionalString("qrLabel"),
                action = block.optionalAction("$path action"),
            )
        }

        null -> error("$path uses unsupported block type \"$type\". Use the advanced JSON editor for this document.")
    }
}

private fun parseCarouselItem(item: JsonObject, path: String): NativeUiCarouselItemDraft {
    ensureOnlyKeys(item, setOf("imageUrl", "title", "subtitle", "contentDescription", "action"), path)
    return NativeUiCarouselItemDraft(
        imageUrl = item.requiredString("imageUrl", path),
        title = item.optionalString("title"),
        subtitle = item.optionalString("subtitle"),
        contentDescription = item.optionalString("contentDescription"),
        action = item.optionalAction("$path action"),
    )
}

private fun parseListItem(item: JsonObject, path: String): NativeUiListItemDraft {
    ensureOnlyKeys(item, setOf("title", "subtitle", "overline", "action"), path)
    return NativeUiListItemDraft(
        title = item.requiredString("title", path),
        subtitle = item.optionalString("subtitle"),
        overline = item.optionalString("overline"),
        action = item.optionalAction("$path action"),
    )
}

private fun JsonObject.optionalAction(path: String): NativeUiActionDraft {
    val action = this["action"] ?: return NativeUiActionDraft()
    val objectValue = action.jsonObject
    val type = objectValue.requiredString("type", path)
    return when (type) {
        "page" -> {
            ensureOnlyKeys(objectValue, setOf("type", "pageId", "slug"), path)
            NativeUiActionDraft(
                type = NativeUiActionType.Page,
                pageId = objectValue.optionalString("pageId"),
                slug = objectValue.optionalString("slug"),
            )
        }

        "url" -> {
            ensureOnlyKeys(objectValue, setOf("type", "url"), path)
            NativeUiActionDraft(
                type = NativeUiActionType.Url,
                url = objectValue.requiredString("url", path),
            )
        }

        "refresh" -> {
            ensureOnlyKeys(objectValue, setOf("type"), path)
            NativeUiActionDraft(type = NativeUiActionType.Refresh)
        }

        "custom" -> {
            ensureOnlyKeys(objectValue, setOf("type", "actionId", "payload"), path)
            NativeUiActionDraft(
                type = NativeUiActionType.Custom,
                actionId = objectValue.requiredString("actionId", path),
                payload = objectValue.optionalString("payload"),
            )
        }

        else -> error("$path uses unsupported action type \"$type\". Use the advanced JSON editor for this document.")
    }
}

private fun createSuggestedDocumentDraft(
    requiredTabRouteIds: List<String>,
    homeTabId: String,
): NativeUiDocumentDraft {
    val pageIds = requiredTabRouteIds.ifEmpty {
        listOf(homeTabId.ifBlank { "home" })
    }
    val rootPageId = when {
        homeTabId.isNotBlank() && homeTabId in pageIds -> homeTabId
        pageIds.isNotEmpty() -> pageIds.first()
        else -> "home"
    }
    return NativeUiDocumentDraft(
        id = "selu-app",
        version = "1.0",
        rootPageId = rootPageId,
        pages = pageIds.map { pageId ->
            NativeUiPageDraft(
                id = pageId,
                title = humanizeRouteId(pageId),
                blocks = listOf(
                    NativeUiTextBlockDraft(
                        text = humanizeRouteId(pageId),
                        variant = NativeUiTextVariant.Title,
                    ),
                    NativeUiTextBlockDraft(
                        text = "Draft content for ${humanizeRouteId(pageId)}.",
                        variant = NativeUiTextVariant.Body,
                    ),
                ),
            )
        },
    )
}

@Composable
internal fun NativeUiDocumentGuidedEditor(
    draft: NativeUiDocumentDraft,
    selectedPageId: String?,
    onSelectedPageChange: (String?) -> Unit,
    requiredTabRouteIds: List<String>,
    homeTabId: String,
    canEdit: Boolean,
    theme: MaterialThemePalette,
    onChange: (NativeUiDocumentDraft) -> Unit,
) {
    val selectedPage = draft.pages.firstOrNull { it.id == selectedPageId }
        ?: draft.pages.firstOrNull { it.id == draft.rootPageId }
        ?: draft.pages.firstOrNull()
    var editingPageId by remember { mutableStateOf<String?>(null) }
    val editingPage = draft.pages.firstOrNull { it.id == editingPageId }
    var selectedEditorPath by remember(editingPage?.id ?: selectedPage?.id) {
        mutableStateOf((editingPage ?: selectedPage)?.let { "page:${it.id}" })
    }
    val missingRequiredTabRouteIds = requiredTabRouteIds.filterNot { requiredId ->
        draft.pages.any { page -> page.id == requiredId }
    }
    val tabPages = draft.pages.filter { it.id in requiredTabRouteIds }
    val subpages = draft.pages.filterNot { it.id in requiredTabRouteIds }
    val pageOutline = editingPage?.let(::buildEditorOutlineEntries).orEmpty()
    val selectedContext = pageOutline.firstOrNull { it.pathKey == selectedEditorPath }
    val selectedPageLabel = editingPage?.title?.ifBlank { editingPage.id } ?: "Page"

    Div(
        attrs = {
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(16.px)
            }
        }
    ) {
        EditorPanel(
            title = "Page Setup",
            description = "Set the app name, choose the first page people land on, and organize the pages used by the app.",
            theme = theme,
        ) {
            FieldRow {
                TextField(
                    theme = theme,
                    label = "Document ID",
                    value = draft.id,
                    placeholder = "selu-app",
                    onValueChange = { if (canEdit) onChange(draft.copy(id = it)) },
                )
                TextField(
                    theme = theme,
                    label = "Version",
                    value = draft.version,
                    placeholder = "1.0",
                    onValueChange = { if (canEdit) onChange(draft.copy(version = it)) },
                )
            }

            StringSelectField(
                theme = theme,
                label = "Root Page",
                value = draft.rootPageId,
                options = draft.pages.map { it.id to (it.title.ifBlank { humanizeRouteId(it.id) } + " (${it.id})") },
                emptyLabel = if (draft.pages.isEmpty()) "Add a page first" else "Select a root page",
                isDisabled = !canEdit || draft.pages.isEmpty(),
                onValueChange = { nextRoot ->
                    if (canEdit && nextRoot.isNotBlank()) {
                        onChange(draft.copy(rootPageId = nextRoot))
                    }
                },
            )

            P(
                attrs = {
                    style {
                        margin(0.px)
                        property("font-size", "0.86rem")
                        property("color", theme.onSurfaceVariant)
                        property("line-height", "1.5")
                    }
                }
            ) {
                Text(
                    if (requiredTabRouteIds.isEmpty()) {
                        "No tab pages have been listed yet."
                    } else {
                        "Tab pages: ${requiredTabRouteIds.joinToString(", ")}. Home tab: \"$homeTabId\"."
                    }
                )
            }
        }

        EditorPanel(
            title = "Pages",
            description = "Open one page at a time to edit it in its own workspace.",
            theme = theme,
        ) {
            if (missingRequiredTabRouteIds.isNotEmpty()) {
                Div(
                    attrs = {
                        style {
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                            gap(10.px)
                        }
                    }
                ) {
                    P(
                        attrs = {
                            style {
                                margin(0.px)
                                property("font-size", "0.8rem")
                                property("font-weight", "700")
                                property("letter-spacing", "0.06em")
                                property("text-transform", "uppercase")
                                property("color", theme.onSurfaceVariant)
                            }
                        }
                    ) {
                        Text("Tabs To Add")
                    }
                    Div(
                        attrs = {
                            style {
                                display(DisplayStyle.Flex)
                                gap(10.px)
                                property("flex-wrap", "wrap")
                            }
                        }
                    ) {
                        missingRequiredTabRouteIds.forEach { missingRouteId ->
                            ActionButton(
                                label = "Add ${humanizeRouteId(missingRouteId)} Tab",
                                onClick = {
                                    if (!canEdit) return@ActionButton
                                    val nextPage = NativeUiPageDraft(
                                        id = missingRouteId,
                                        title = humanizeRouteId(missingRouteId),
                                        blocks = listOf(
                                            NativeUiTextBlockDraft(
                                                text = humanizeRouteId(missingRouteId),
                                                variant = NativeUiTextVariant.Title,
                                            ),
                                            NativeUiTextBlockDraft(
                                                text = "Draft content for ${humanizeRouteId(missingRouteId)}.",
                                                variant = NativeUiTextVariant.Body,
                                            ),
                                        ),
                                    )
                                    onChange(draft.copy(pages = draft.pages + nextPage))
                                    editingPageId = nextPage.id
                                    selectedEditorPath = "page:${nextPage.id}"
                                    onSelectedPageChange(nextPage.id)
                                },
                                theme = theme,
                                isDisabled = !canEdit,
                                isPrimary = false,
                            )
                        }
                    }
                }
            }

            if (tabPages.isNotEmpty()) {
                PageGroupSection(
                    title = "Tab Pages",
                    description = "These pages sit directly in the app tab bar.",
                    pages = tabPages,
                    selectedPageId = selectedPage?.id,
                    rootPageId = draft.rootPageId,
                    requiredTabRouteIds = requiredTabRouteIds,
                    theme = theme,
                    onPageSelected = { pageId ->
                        editingPageId = pageId
                        selectedEditorPath = "page:$pageId"
                        onSelectedPageChange(pageId)
                    },
                )
            }

            if (subpages.isNotEmpty()) {
                PageGroupSection(
                    title = "Subpages",
                    description = "These pages stay off the tab bar and open from buttons, cards, and links.",
                    pages = subpages,
                    selectedPageId = selectedPage?.id,
                    rootPageId = draft.rootPageId,
                    requiredTabRouteIds = requiredTabRouteIds,
                    theme = theme,
                    onPageSelected = { pageId ->
                        editingPageId = pageId
                        selectedEditorPath = "page:$pageId"
                        onSelectedPageChange(pageId)
                    },
                )
            }

            if (draft.pages.isEmpty()) {
                P(
                    attrs = {
                        style {
                            margin(0.px)
                            padding(14.px)
                            borderRadius(16.px)
                            property("background", theme.surface)
                            property("border", "1px dashed ${theme.outlineVariant}")
                            property("color", theme.onSurfaceVariant)
                            property("line-height", "1.5")
                        }
                    }
                ) {
                    Text("No pages yet. Add a tab page or a linked page to get started.")
                }
            }

            P(
                attrs = {
                    style {
                        margin(0.px)
                        property("font-size", "0.88rem")
                        property("line-height", "1.5")
                        property("color", theme.onSurfaceVariant)
                    }
                }
            ) {
                Text("Tab pages only control the main tabs. Any button can still open any page in this list.")
            }

            ActionButton(
                label = "Add Linked Page",
                onClick = {
                    if (!canEdit) return@ActionButton
                    val nextId = nextAvailablePageId(draft.pages)
                    val nextPage = NativeUiPageDraft(
                        id = nextId,
                        title = humanizeRouteId(nextId),
                        blocks = listOf(
                            NativeUiTextBlockDraft(
                                text = humanizeRouteId(nextId),
                                variant = NativeUiTextVariant.Title,
                            ),
                        ),
                    )
                    val nextPages = draft.pages + nextPage
                    val nextRoot = draft.rootPageId.ifBlank { nextPage.id }
                    onChange(draft.copy(pages = nextPages, rootPageId = nextRoot))
                    editingPageId = nextPage.id
                    selectedEditorPath = "page:${nextPage.id}"
                    onSelectedPageChange(nextPage.id)
                },
                theme = theme,
                isDisabled = !canEdit,
                isPrimary = false,
            )
        }
    }

    if (editingPage != null) {
        PageEditorModal(
            page = editingPage,
            draft = draft,
            requiredTabRouteIds = requiredTabRouteIds,
            selectedContext = selectedContext,
            selectedPageLabel = selectedPageLabel,
            selectedPath = selectedEditorPath,
            canEdit = canEdit,
            theme = theme,
            onClose = { editingPageId = null },
            onSelectedPathChange = { selectedEditorPath = it },
            onSelectedPageChange = onSelectedPageChange,
            onChange = onChange,
        )
    }
}

@Composable
private fun PageEditorModal(
    page: NativeUiPageDraft,
    draft: NativeUiDocumentDraft,
    requiredTabRouteIds: List<String>,
    selectedContext: EditorOutlineEntry?,
    selectedPageLabel: String,
    selectedPath: String?,
    canEdit: Boolean,
    theme: MaterialThemePalette,
    onClose: () -> Unit,
    onSelectedPathChange: (String) -> Unit,
    onSelectedPageChange: (String?) -> Unit,
    onChange: (NativeUiDocumentDraft) -> Unit,
) {
    Div(
        attrs = {
            style {
                property("position", "fixed")
                property("inset", "0")
                property("z-index", "40")
                property("background", "rgba(15, 23, 42, 0.56)")
                padding(24.px)
                property("overflow-y", "auto")
            }
        }
    ) {
            Div(
                attrs = {
                    style {
                        property("max-width", "1280px")
                        property("margin", "0 auto")
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(18.px)
                    padding(22.px)
                    borderRadius(22.px)
                    property("background", theme.surface)
                    property("border", "1px solid ${theme.outlineVariant}")
                    property("box-shadow", "0 28px 60px ${theme.shadow}")
                }
            }
        ) {
            FieldRow {
                Div(
                    attrs = {
                        style {
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                            gap(8.px)
                            flex(1)
                        }
                    }
                ) {
                    P(
                        attrs = {
                            style {
                                margin(0.px)
                                property("font-size", "0.76rem")
                                property("font-weight", "700")
                                property("letter-spacing", "0.08em")
                                property("text-transform", "uppercase")
                                property("color", theme.onSurfaceVariant)
                            }
                        }
                    ) {
                        Text("Page Editor")
                    }
                    P(
                        attrs = {
                            style {
                                margin(0.px)
                                property("font-size", "1.2rem")
                                property("font-weight", "700")
                                property("color", theme.onSurface)
                            }
                        }
                    ) {
                        Text(selectedContext?.title ?: selectedPageLabel)
                    }
                    P(
                        attrs = {
                            style {
                                margin(0.px)
                                property("font-size", "0.9rem")
                                property("line-height", "1.5")
                                property("color", theme.onSurfaceVariant)
                            }
                        }
                    ) {
                        Text(
                            selectedContext?.breadcrumb
                                ?: "${nativeUiPageKindLabel(page, draft.rootPageId, requiredTabRouteIds)}. Edit this page without the rest of the settings screen getting in the way."
                        )
                    }
                }
                ActionButton(
                    label = "Done",
                    onClick = onClose,
                    theme = theme,
                    isPrimary = false,
                )
            }

            Div(
                attrs = {
                    style {
                        display(DisplayStyle.Flex)
                        gap(16.px)
                        property("flex-wrap", "wrap")
                        property("align-items", "stretch")
                    }
                }
            ) {
                EditorPanel(
                    title = "Page Details",
                    description = "Start with the page name and how it should be used.",
                    theme = theme,
                ) {
                    FieldRow {
                        TextField(
                            theme = theme,
                            label = "Page ID",
                            value = page.id,
                            placeholder = "home",
                            onValueChange = { nextId ->
                                if (!canEdit) return@TextField
                                val updatedPages = draft.pages.map { currentPage ->
                                    if (currentPage.id == page.id) currentPage.copy(id = nextId) else currentPage
                                }
                                val updatedRoot = if (draft.rootPageId == page.id) nextId else draft.rootPageId
                                onChange(draft.copy(pages = updatedPages, rootPageId = updatedRoot))
                                onSelectedPathChange("page:$nextId")
                                onSelectedPageChange(nextId)
                            },
                        )
                        TextField(
                            theme = theme,
                            label = "Page Title",
                            value = page.title,
                            placeholder = humanizeRouteId(page.id),
                            onValueChange = { nextTitle ->
                                if (canEdit) {
                                    onChange(
                                        draft.copy(
                                            pages = draft.pages.map { currentPage ->
                                                if (currentPage.id == page.id) currentPage.copy(title = nextTitle) else currentPage
                                            },
                                        )
                                    )
                                }
                            },
                        )
                    }

                    TextField(
                        theme = theme,
                        label = "Page Slug",
                        value = page.slug,
                        placeholder = "optional-page-slug",
                        onValueChange = { nextSlug ->
                            if (canEdit) {
                                onChange(
                                    draft.copy(
                                        pages = draft.pages.map { currentPage ->
                                            if (currentPage.id == page.id) currentPage.copy(slug = nextSlug) else currentPage
                                        },
                                    )
                                )
                            }
                        },
                    )

                    P(
                        attrs = {
                            style {
                                margin(0.px)
                                property("font-size", "0.88rem")
                                property("line-height", "1.5")
                                property("color", theme.onSurfaceVariant)
                            }
                        }
                    ) {
                        Text("Tab pages show in the app tabs. Linked pages stay off the tab bar but can still open from buttons and cards.")
                    }

                    FieldRow {
                        ActionButton(
                            label = if (page.id == draft.rootPageId) "Starting Page" else "Make Starting Page",
                            onClick = { if (canEdit) onChange(draft.copy(rootPageId = page.id)) },
                            theme = theme,
                            isDisabled = !canEdit || page.id == draft.rootPageId,
                            isPrimary = false,
                        )
                        ActionButton(
                            label = "Delete Page",
                            onClick = {
                                if (!canEdit) return@ActionButton
                                val remainingPages = draft.pages.filterNot { it.id == page.id }
                                val nextRoot = when {
                                    remainingPages.isEmpty() -> ""
                                    draft.rootPageId == page.id -> remainingPages.first().id
                                    else -> draft.rootPageId
                                }
                                onChange(draft.copy(pages = remainingPages, rootPageId = nextRoot))
                                onSelectedPageChange(remainingPages.firstOrNull()?.id)
                                onClose()
                            },
                            theme = theme,
                            isDisabled = !canEdit,
                            isPrimary = false,
                        )
                    }
                }
            }

            NativeUiBlockListEditor(
                label = "Page Content",
                parentPath = "page:${page.id}",
                parentLabel = page.title.ifBlank { humanizeRouteId(page.id) },
                blocks = page.blocks,
                availablePages = draft.pages,
                selectedPath = selectedPath,
                onSelectedPathChange = onSelectedPathChange,
                canEdit = canEdit,
                theme = theme,
                onChange = { nextBlocks ->
                    onChange(
                        draft.copy(
                            pages = draft.pages.map { currentPage ->
                                if (currentPage.id == page.id) currentPage.copy(blocks = nextBlocks) else currentPage
                            },
                        )
                    )
                },
            )

            PageStructurePreviewPanel(
                page = page,
                selectedPath = selectedPath,
                theme = theme,
            )
        }
    }
}

@Composable
private fun PageGroupSection(
    title: String,
    description: String,
    pages: List<NativeUiPageDraft>,
    selectedPageId: String?,
    rootPageId: String,
    requiredTabRouteIds: List<String>,
    theme: MaterialThemePalette,
    onPageSelected: (String) -> Unit,
) {
    Div(
        attrs = {
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(10.px)
            }
        }
    ) {
        P(
            attrs = {
                style {
                    margin(0.px)
                    property("font-size", "0.8rem")
                    property("font-weight", "700")
                    property("letter-spacing", "0.06em")
                    property("text-transform", "uppercase")
                    property("color", theme.onSurfaceVariant)
                }
            }
        ) {
            Text(title)
        }
        P(
            attrs = {
                style {
                    margin(0.px)
                    property("font-size", "0.9rem")
                    property("line-height", "1.5")
                    property("color", theme.onSurfaceVariant)
                }
            }
        ) {
            Text(description)
        }
        Div(
            attrs = {
                style {
                    display(DisplayStyle.Flex)
                    gap(12.px)
                    property("flex-wrap", "wrap")
                }
            }
        ) {
            pages.forEach { page ->
                PageChip(
                    page = page,
                    isSelected = selectedPageId == page.id,
                    isRoot = page.id == rootPageId,
                    requiredTabRouteIds = requiredTabRouteIds,
                    theme = theme,
                    onClick = { onPageSelected(page.id) },
                )
            }
        }
    }
}

@Composable
private fun PageStructurePreviewPanel(
    page: NativeUiPageDraft,
    selectedPath: String?,
    theme: MaterialThemePalette,
) {
    Div(
        attrs = {
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(12.px)
                padding(16.px)
                borderRadius(18.px)
                property("background", theme.surface)
                property("border", "1px solid ${theme.outlineVariant}")
            }
        }
    ) {
        P(
            attrs = {
                style {
                    margin(0.px)
                    property("font-size", "0.78rem")
                    property("font-weight", "700")
                    property("letter-spacing", "0.08em")
                    property("text-transform", "uppercase")
                    property("color", theme.onSurfaceVariant)
                }
            }
        ) {
            Text("Page Preview")
        }
        P(
            attrs = {
                style {
                    margin(0.px)
                    property("font-size", "0.9rem")
                    property("line-height", "1.5")
                    property("color", theme.onSurfaceVariant)
                }
            }
        ) {
            Text("This preview updates from your unsaved changes so you can spot problems before you save.")
        }
        Div(
            attrs = {
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(12.px)
                    padding(14.px)
                    borderRadius(16.px)
                    property("background", theme.surfaceContainerHighest)
                    property("border", "1px solid ${theme.outlineVariant}")
                }
            }
        ) {
            P(
                attrs = {
                    style {
                        margin(0.px)
                        property("font-size", "0.72rem")
                        property("font-weight", "700")
                        property("letter-spacing", "0.08em")
                        property("text-transform", "uppercase")
                        property("color", theme.onSurfaceVariant)
                    }
                }
            ) {
                Text("Preview Page")
            }
            P(
                attrs = {
                    style {
                        margin(0.px)
                        property("font-size", "1.02rem")
                        property("font-weight", "700")
                        property("color", theme.onSurface)
                    }
                }
            ) {
                Text(page.title.ifBlank { humanizeRouteId(page.id) })
            }

            if (page.blocks.isEmpty()) {
                P(
                    attrs = {
                        style {
                            margin(0.px)
                            padding(12.px)
                            borderRadius(14.px)
                            property("background", theme.surface)
                            property("border", "1px dashed ${theme.outlineVariant}")
                            property("color", theme.onSurfaceVariant)
                        }
                    }
                ) {
                    Text("No page content yet.")
                }
            } else {
                page.blocks.forEachIndexed { index, block ->
                    PreviewBlockNode(
                        block = block,
                        pathKey = "page:${page.id}/block:$index",
                        selectedPath = selectedPath,
                        theme = theme,
                    )
                }
            }
        }
    }
}

@Composable
private fun NativeUiBlockListEditor(
    label: String,
    parentPath: String,
    parentLabel: String,
    blocks: List<NativeUiBlockDraft>,
    availablePages: List<NativeUiPageDraft>,
    selectedPath: String?,
    onSelectedPathChange: (String) -> Unit,
    canEdit: Boolean,
    theme: MaterialThemePalette,
    onChange: (List<NativeUiBlockDraft>) -> Unit,
) {
    var nextBlockType by remember(parentPath) { mutableStateOf(NativeUiBlockType.Text) }

    Div(
        attrs = {
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(14.px)
            }
        }
    ) {
        P(
            attrs = {
                style {
                    margin(0.px)
                    property("font-size", "0.82rem")
                    property("font-weight", "700")
                    property("letter-spacing", "0.06em")
                    property("text-transform", "uppercase")
                    property("color", theme.onSurfaceVariant)
                }
            }
        ) {
            Text(label)
        }

        if (blocks.isEmpty()) {
            P(
                attrs = {
                    style {
                        margin(0.px)
                        padding(14.px)
                        borderRadius(16.px)
                        property("background", theme.surfaceContainerHighest)
                        property("border", "1px dashed ${theme.outlineVariant}")
                        property("color", theme.onSurfaceVariant)
                        property("line-height", "1.5")
                    }
                }
            ) {
                Text("No content blocks yet. Add one below.")
            }
        }

        blocks.forEachIndexed { index, block ->
            BlockEditorCard(
                block = block,
                pathKey = "$parentPath/block:$index",
                contextLabel = "$parentLabel / ${block.type.label} ${index + 1}",
                index = index,
                totalBlocks = blocks.size,
                availablePages = availablePages,
                selectedPath = selectedPath,
                onSelectedPathChange = onSelectedPathChange,
                canEdit = canEdit,
                theme = theme,
                onChange = { nextBlock ->
                    onChange(blocks.replaceAt(index, nextBlock))
                },
                onMoveUp = { onChange(blocks.move(fromIndex = index, toIndex = index - 1)) },
                onMoveDown = { onChange(blocks.move(fromIndex = index, toIndex = index + 1)) },
                onRemove = { onChange(blocks.removeAt(index)) },
            )
        }

        FieldRow {
            StringSelectField(
                theme = theme,
                label = "Block Type",
                value = nextBlockType.wireValue,
                options = NativeUiBlockType.entries.map { it.wireValue to it.label },
                emptyLabel = "Choose block type",
                isDisabled = !canEdit,
                onValueChange = { selected ->
                    NativeUiBlockType.entries.firstOrNull { it.wireValue == selected }?.let { nextBlockType = it }
                },
            )
            ActionButton(
                label = "Add Block",
                onClick = { if (canEdit) onChange(blocks + defaultBlockDraft(nextBlockType)) },
                theme = theme,
                isDisabled = !canEdit,
                isPrimary = false,
            )
        }
    }
}

@Composable
private fun BlockEditorCard(
    block: NativeUiBlockDraft,
    pathKey: String,
    contextLabel: String,
    index: Int,
    totalBlocks: Int,
    availablePages: List<NativeUiPageDraft>,
    selectedPath: String?,
    onSelectedPathChange: (String) -> Unit,
    canEdit: Boolean,
    theme: MaterialThemePalette,
    onChange: (NativeUiBlockDraft) -> Unit,
    onMoveUp: () -> Unit,
    onMoveDown: () -> Unit,
    onRemove: () -> Unit,
) {
    val isSelected = selectedPath == pathKey
    val hasSelectedDescendant = selectedPath?.startsWith("$pathKey/") == true
    var isExpanded by remember(pathKey) {
        mutableStateOf(pathKey.count { it == '/' } <= 1)
    }
    EditorPanel(
        title = blockDisplayTitle(block),
        description = "$contextLabel. Block ${index + 1} of $totalBlocks.",
        theme = theme,
        isEmphasized = isSelected || hasSelectedDescendant,
        actions = {
            Div(
                attrs = {
                    style {
                        display(DisplayStyle.Flex)
                        gap(8.px)
                        property("flex-wrap", "wrap")
                    }
                }
            ) {
                ActionButton(
                    label = if (isSelected) "Focused" else "Focus",
                    onClick = { onSelectedPathChange(pathKey) },
                    theme = theme,
                    isDisabled = isSelected,
                    isPrimary = false,
                )
                ActionButton(
                    label = if (isExpanded || hasSelectedDescendant) "Collapse" else "Expand",
                    onClick = { isExpanded = !isExpanded },
                    theme = theme,
                    isDisabled = false,
                    isPrimary = false,
                )
                ActionButton(
                    label = "Move Up",
                    onClick = onMoveUp,
                    theme = theme,
                    isDisabled = !canEdit || index == 0,
                    isPrimary = false,
                )
                ActionButton(
                    label = "Move Down",
                    onClick = onMoveDown,
                    theme = theme,
                    isDisabled = !canEdit || index == totalBlocks - 1,
                    isPrimary = false,
                )
                ActionButton(
                    label = "Remove",
                    onClick = onRemove,
                    theme = theme,
                    isDisabled = !canEdit,
                    isPrimary = false,
                )
            }
        },
    ) {
        P(
            attrs = {
                style {
                    margin(0.px)
                    property("font-size", "0.82rem")
                    property("color", theme.onSurfaceVariant)
                    property("line-height", "1.45")
                }
            }
        ) {
            Text(blockMeta(block))
        }

        if (!(isExpanded || hasSelectedDescendant)) {
            return@EditorPanel
        }

        TextField(
            theme = theme,
            label = "Block ID",
            value = block.id,
            placeholder = "optional-block-id",
            onValueChange = { nextId -> if (canEdit) onChange(updateBlockId(block, nextId)) },
        )

        when (block) {
            is NativeUiTextBlockDraft -> {
                FieldRow {
                    TextField(
                        theme = theme,
                        label = "Text",
                        value = block.text,
                        placeholder = "Enter text",
                        onValueChange = { nextText -> if (canEdit) onChange(block.copy(text = nextText)) },
                    )
                    StringSelectField(
                        theme = theme,
                        label = "Variant",
                        value = block.variant.wireValue,
                        options = NativeUiTextVariant.entries.map { it.wireValue to it.label },
                        isDisabled = !canEdit,
                        onValueChange = { nextVariant ->
                            if (canEdit) {
                                onChange(
                                    block.copy(
                                        variant = NativeUiTextVariant.entries.first { it.wireValue == nextVariant }
                                    )
                                )
                            }
                        },
                    )
                }
                ColorRoleField(
                    theme = theme,
                    label = "Color Role",
                    value = block.colorRole,
                    canEdit = canEdit,
                    onValueChange = { nextRole -> onChange(block.copy(colorRole = nextRole)) },
                )
            }

            is NativeUiImageBlockDraft -> {
                FieldRow {
                    TextField(
                        theme = theme,
                        label = "Image URL",
                        value = block.url,
                        placeholder = "https://example.com/image.png",
                        onValueChange = { nextUrl -> if (canEdit) onChange(block.copy(url = nextUrl)) },
                    )
                    TextField(
                        theme = theme,
                        label = "Aspect Ratio",
                        value = block.aspectRatio,
                        placeholder = "1.777",
                        inputTypeOverride = "number",
                        step = "0.1",
                        onValueChange = { nextRatio -> if (canEdit) onChange(block.copy(aspectRatio = nextRatio)) },
                    )
                }
                TextField(
                    theme = theme,
                    label = "Content Description",
                    value = block.contentDescription,
                    placeholder = "Accessible description",
                    onValueChange = { nextDescription -> if (canEdit) onChange(block.copy(contentDescription = nextDescription)) },
                )
            }

            is NativeUiButtonBlockDraft -> {
                FieldRow {
                    TextField(
                        theme = theme,
                        label = "Label",
                        value = block.label,
                        placeholder = "Primary action",
                        onValueChange = { nextLabel -> if (canEdit) onChange(block.copy(label = nextLabel)) },
                    )
                    StringSelectField(
                        theme = theme,
                        label = "Style",
                        value = block.style.wireValue,
                        options = NativeUiButtonStyleDraft.entries.map { it.wireValue to it.label },
                        isDisabled = !canEdit,
                        onValueChange = { nextStyle ->
                            if (canEdit) {
                                onChange(
                                    block.copy(
                                        style = NativeUiButtonStyleDraft.entries.first { it.wireValue == nextStyle }
                                    )
                                )
                            }
                        },
                    )
                }
                ColorRoleField(
                    theme = theme,
                    label = "Container Color Role",
                    value = block.containerColorRole,
                    canEdit = canEdit,
                    onValueChange = { nextRole -> onChange(block.copy(containerColorRole = nextRole)) },
                )
                FieldRow {
                    ColorRoleField(
                        theme = theme,
                        label = "Content Color Role",
                        value = block.contentColorRole,
                        canEdit = canEdit,
                        onValueChange = { nextRole -> onChange(block.copy(contentColorRole = nextRole)) },
                    )
                    ColorRoleField(
                        theme = theme,
                        label = "Border Color Role",
                        value = block.borderColorRole,
                        canEdit = canEdit,
                        onValueChange = { nextRole -> onChange(block.copy(borderColorRole = nextRole)) },
                    )
                }
                ActionEditor(
                    action = block.action,
                    availablePages = availablePages,
                    canEdit = canEdit,
                    theme = theme,
                    onChange = { nextAction -> onChange(block.copy(action = nextAction)) },
                )
            }

            is NativeUiSpacerBlockDraft -> {
                TextField(
                    theme = theme,
                    label = "Height (dp)",
                    value = block.heightDp.toString(),
                    placeholder = "24",
                    inputTypeOverride = "number",
                    step = "1",
                    onValueChange = { nextHeight ->
                        if (canEdit) {
                            onChange(block.copy(heightDp = nextHeight.toIntOrNull() ?: 0))
                        }
                    },
                )
            }

            is NativeUiSectionBlockDraft -> {
                TextField(
                    theme = theme,
                    label = "Section Title",
                    value = block.title,
                    placeholder = "Section heading",
                    onValueChange = { nextTitle -> if (canEdit) onChange(block.copy(title = nextTitle)) },
                )
                NativeUiBlockListEditor(
                    label = "Section Content",
                    parentPath = pathKey,
                    parentLabel = block.title.ifBlank { contextLabel },
                    blocks = block.blocks,
                    availablePages = availablePages,
                    selectedPath = selectedPath,
                    onSelectedPathChange = onSelectedPathChange,
                    canEdit = canEdit,
                    theme = theme,
                    onChange = { nextBlocks -> onChange(block.copy(blocks = nextBlocks)) },
                )
            }

            is NativeUiCardBlockDraft -> {
                FieldRow {
                    TextField(
                        theme = theme,
                        label = "Title",
                        value = block.title,
                        placeholder = "Card title",
                        onValueChange = { nextTitle -> if (canEdit) onChange(block.copy(title = nextTitle)) },
                    )
                    StringSelectField(
                        theme = theme,
                        label = "Style",
                        value = block.style.wireValue,
                        options = NativeUiCardStyleDraft.entries.map { it.wireValue to it.label },
                        isDisabled = !canEdit,
                        onValueChange = { nextStyle ->
                            if (canEdit) {
                                onChange(
                                    block.copy(
                                        style = NativeUiCardStyleDraft.entries.first { it.wireValue == nextStyle }
                                    )
                                )
                            }
                        },
                    )
                }
                TextField(
                    theme = theme,
                    label = "Body",
                    value = block.body,
                    placeholder = "Card body text",
                    onValueChange = { nextBody -> if (canEdit) onChange(block.copy(body = nextBody)) },
                )
                ColorRoleField(
                    theme = theme,
                    label = "Container Color Role",
                    value = block.containerColorRole,
                    canEdit = canEdit,
                    onValueChange = { nextRole -> onChange(block.copy(containerColorRole = nextRole)) },
                )
                FieldRow {
                    ColorRoleField(
                        theme = theme,
                        label = "Content Color Role",
                        value = block.contentColorRole,
                        canEdit = canEdit,
                        onValueChange = { nextRole -> onChange(block.copy(contentColorRole = nextRole)) },
                    )
                    ColorRoleField(
                        theme = theme,
                        label = "Border Color Role",
                        value = block.borderColorRole,
                        canEdit = canEdit,
                        onValueChange = { nextRole -> onChange(block.copy(borderColorRole = nextRole)) },
                    )
                }
                ActionEditor(
                    action = block.action,
                    availablePages = availablePages,
                    canEdit = canEdit,
                    theme = theme,
                    onChange = { nextAction -> onChange(block.copy(action = nextAction)) },
                )
                NativeUiBlockListEditor(
                    label = "Card Content",
                    parentPath = pathKey,
                    parentLabel = block.title.ifBlank { contextLabel },
                    blocks = block.blocks,
                    availablePages = availablePages,
                    selectedPath = selectedPath,
                    onSelectedPathChange = onSelectedPathChange,
                    canEdit = canEdit,
                    theme = theme,
                    onChange = { nextBlocks -> onChange(block.copy(blocks = nextBlocks)) },
                )
            }

            is NativeUiCarouselBlockDraft -> {
                NativeUiCarouselItemsEditor(
                    pathKey = pathKey,
                    parentLabel = contextLabel,
                    items = block.items,
                    availablePages = availablePages,
                    selectedPath = selectedPath,
                    onSelectedPathChange = onSelectedPathChange,
                    canEdit = canEdit,
                    theme = theme,
                    onChange = { nextItems -> onChange(block.copy(items = nextItems)) },
                )
            }

            is NativeUiListBlockDraft -> {
                NativeUiListItemsEditor(
                    pathKey = pathKey,
                    parentLabel = contextLabel,
                    items = block.items,
                    availablePages = availablePages,
                    selectedPath = selectedPath,
                    onSelectedPathChange = onSelectedPathChange,
                    canEdit = canEdit,
                    theme = theme,
                    onChange = { nextItems -> onChange(block.copy(items = nextItems)) },
                )
            }

            is NativeUiEventCardBlockDraft -> {
                FieldRow {
                    TextField(
                        theme = theme,
                        label = "Title",
                        value = block.title,
                        placeholder = "Event title",
                        onValueChange = { nextTitle -> if (canEdit) onChange(block.copy(title = nextTitle)) },
                    )
                    TextField(
                        theme = theme,
                        label = "Subtitle",
                        value = block.subtitle,
                        placeholder = "Optional subtitle",
                        onValueChange = { nextSubtitle -> if (canEdit) onChange(block.copy(subtitle = nextSubtitle)) },
                    )
                }
                FieldRow {
                    TextField(
                        theme = theme,
                        label = "Date Label",
                        value = block.dateLabel,
                        placeholder = "Fri, Oct 10",
                        onValueChange = { nextDate -> if (canEdit) onChange(block.copy(dateLabel = nextDate)) },
                    )
                    TextField(
                        theme = theme,
                        label = "Location Label",
                        value = block.locationLabel,
                        placeholder = "Gymnasium",
                        onValueChange = { nextLocation -> if (canEdit) onChange(block.copy(locationLabel = nextLocation)) },
                    )
                }
                TextField(
                    theme = theme,
                    label = "Image URL",
                    value = block.imageUrl,
                    placeholder = "https://example.com/event.png",
                    onValueChange = { nextUrl -> if (canEdit) onChange(block.copy(imageUrl = nextUrl)) },
                )
                ActionEditor(
                    action = block.action,
                    availablePages = availablePages,
                    canEdit = canEdit,
                    theme = theme,
                    onChange = { nextAction -> onChange(block.copy(action = nextAction)) },
                )
            }

            is NativeUiTicketCardBlockDraft -> {
                FieldRow {
                    TextField(
                        theme = theme,
                        label = "Title",
                        value = block.title,
                        placeholder = "Ticket title",
                        onValueChange = { nextTitle -> if (canEdit) onChange(block.copy(title = nextTitle)) },
                    )
                    TextField(
                        theme = theme,
                        label = "Subtitle",
                        value = block.subtitle,
                        placeholder = "Optional subtitle",
                        onValueChange = { nextSubtitle -> if (canEdit) onChange(block.copy(subtitle = nextSubtitle)) },
                    )
                }
                FieldRow {
                    TextField(
                        theme = theme,
                        label = "Status",
                        value = block.status,
                        placeholder = "Admitted",
                        onValueChange = { nextStatus -> if (canEdit) onChange(block.copy(status = nextStatus)) },
                    )
                    TextField(
                        theme = theme,
                        label = "Holder Name",
                        value = block.holderName,
                        placeholder = "Jordan Lee",
                        onValueChange = { nextHolder -> if (canEdit) onChange(block.copy(holderName = nextHolder)) },
                    )
                }
                TextField(
                    theme = theme,
                    label = "QR Label",
                    value = block.qrLabel,
                    placeholder = "A1B2-C3D4",
                    onValueChange = { nextLabel -> if (canEdit) onChange(block.copy(qrLabel = nextLabel)) },
                )
                ActionEditor(
                    action = block.action,
                    availablePages = availablePages,
                    canEdit = canEdit,
                    theme = theme,
                    onChange = { nextAction -> onChange(block.copy(action = nextAction)) },
                )
            }
        }
    }
}

@Composable
private fun NativeUiCarouselItemsEditor(
    pathKey: String,
    parentLabel: String,
    items: List<NativeUiCarouselItemDraft>,
    availablePages: List<NativeUiPageDraft>,
    selectedPath: String?,
    onSelectedPathChange: (String) -> Unit,
    canEdit: Boolean,
    theme: MaterialThemePalette,
    onChange: (List<NativeUiCarouselItemDraft>) -> Unit,
) {
    Div(
        attrs = {
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(12.px)
            }
        }
    ) {
        if (items.isEmpty()) {
            P(
                attrs = {
                    style {
                        margin(0.px)
                        padding(14.px)
                        borderRadius(16.px)
                        property("background", theme.surfaceContainerHighest)
                        property("border", "1px dashed ${theme.outlineVariant}")
                        property("color", theme.onSurfaceVariant)
                    }
                }
            ) {
                Text("Carousel blocks need at least one image item.")
            }
        }

        items.forEachIndexed { index, item ->
            val itemPath = "$pathKey/item:$index"
            val isSelected = selectedPath == itemPath
            var isExpanded by remember(itemPath) { mutableStateOf(index == 0) }
            EditorPanel(
                title = item.title.ifBlank { "Carousel Item ${index + 1}" },
                description = "$parentLabel / Carousel Item ${index + 1}. Add the image, text, and link for this slide.",
                theme = theme,
                isEmphasized = isSelected,
                actions = {
                    Div(
                        attrs = {
                            style {
                                display(DisplayStyle.Flex)
                                gap(8.px)
                                property("flex-wrap", "wrap")
                            }
                        }
                    ) {
                        ActionButton(
                            label = if (isSelected) "Focused" else "Focus",
                            onClick = { onSelectedPathChange(itemPath) },
                            theme = theme,
                            isDisabled = isSelected,
                            isPrimary = false,
                        )
                        ActionButton(
                            label = if (isExpanded) "Collapse" else "Expand",
                            onClick = { isExpanded = !isExpanded },
                            theme = theme,
                            isDisabled = false,
                            isPrimary = false,
                        )
                        ActionButton(
                            label = "Remove",
                            onClick = { onChange(items.removeAt(index)) },
                            theme = theme,
                            isDisabled = !canEdit || items.size <= 1,
                            isPrimary = false,
                        )
                    }
                },
            ) {
                if (!isExpanded) {
                    return@EditorPanel
                }

                TextField(
                    theme = theme,
                    label = "Image URL",
                    value = item.imageUrl,
                    placeholder = "https://example.com/highlight.jpg",
                    onValueChange = { nextUrl ->
                        if (canEdit) onChange(items.replaceAt(index, item.copy(imageUrl = nextUrl)))
                    },
                )
                FieldRow {
                    TextField(
                        theme = theme,
                        label = "Title",
                        value = item.title,
                        placeholder = "Live from the student section",
                        onValueChange = { nextTitle ->
                            if (canEdit) onChange(items.replaceAt(index, item.copy(title = nextTitle)))
                        },
                    )
                    TextField(
                        theme = theme,
                        label = "Subtitle",
                        value = item.subtitle,
                        placeholder = "Game-night highlights can update during the event.",
                        onValueChange = { nextSubtitle ->
                            if (canEdit) onChange(items.replaceAt(index, item.copy(subtitle = nextSubtitle)))
                        },
                    )
                }
                TextField(
                    theme = theme,
                    label = "Content Description",
                    value = item.contentDescription,
                    placeholder = "Accessible image description",
                    onValueChange = { nextDescription ->
                        if (canEdit) onChange(items.replaceAt(index, item.copy(contentDescription = nextDescription)))
                    },
                )
                ActionEditor(
                    action = item.action,
                    availablePages = availablePages,
                    canEdit = canEdit,
                    theme = theme,
                    onChange = { nextAction ->
                        onChange(items.replaceAt(index, item.copy(action = nextAction)))
                    },
                )
            }
        }

        ActionButton(
            label = "Add Carousel Item",
            onClick = {
                if (canEdit) {
                    onChange(
                        items + NativeUiCarouselItemDraft(
                            imageUrl = "https://example.com/highlight.jpg",
                            title = "New highlight",
                        )
                    )
                }
            },
            theme = theme,
            isDisabled = !canEdit,
            isPrimary = false,
        )
    }
}

@Composable
private fun NativeUiListItemsEditor(
    pathKey: String,
    parentLabel: String,
    items: List<NativeUiListItemDraft>,
    availablePages: List<NativeUiPageDraft>,
    selectedPath: String?,
    onSelectedPathChange: (String) -> Unit,
    canEdit: Boolean,
    theme: MaterialThemePalette,
    onChange: (List<NativeUiListItemDraft>) -> Unit,
) {
    Div(
        attrs = {
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(12.px)
            }
        }
    ) {
        if (items.isEmpty()) {
            P(
                attrs = {
                    style {
                        margin(0.px)
                        padding(14.px)
                        borderRadius(16.px)
                        property("background", theme.surfaceContainerHighest)
                        property("border", "1px dashed ${theme.outlineVariant}")
                        property("color", theme.onSurfaceVariant)
                    }
                }
            ) {
                Text("No list items yet.")
            }
        }

        items.forEachIndexed { index, item ->
            val itemPath = "$pathKey/item:$index"
            val isSelected = selectedPath == itemPath
            var isExpanded by remember(itemPath) { mutableStateOf(index == 0) }
            EditorPanel(
                title = item.title.ifBlank { "List Item ${index + 1}" },
                description = "$parentLabel / List Item ${index + 1}.",
                theme = theme,
                isEmphasized = isSelected,
                actions = {
                    Div(
                        attrs = {
                            style {
                                display(DisplayStyle.Flex)
                                gap(8.px)
                                property("flex-wrap", "wrap")
                            }
                        }
                    ) {
                        ActionButton(
                            label = if (isSelected) "Focused" else "Focus",
                            onClick = { onSelectedPathChange(itemPath) },
                            theme = theme,
                            isDisabled = isSelected,
                            isPrimary = false,
                        )
                        ActionButton(
                            label = if (isExpanded) "Collapse" else "Expand",
                            onClick = { isExpanded = !isExpanded },
                            theme = theme,
                            isDisabled = false,
                            isPrimary = false,
                        )
                        ActionButton(
                            label = "Remove",
                            onClick = { onChange(items.removeAt(index)) },
                            theme = theme,
                            isDisabled = !canEdit,
                            isPrimary = false,
                        )
                    }
                },
            ) {
                if (!isExpanded) {
                    return@EditorPanel
                }

                FieldRow {
                    TextField(
                        theme = theme,
                        label = "Title",
                        value = item.title,
                        placeholder = "Item title",
                        onValueChange = { nextTitle ->
                            if (canEdit) onChange(items.replaceAt(index, item.copy(title = nextTitle)))
                        },
                    )
                    TextField(
                        theme = theme,
                        label = "Overline",
                        value = item.overline,
                        placeholder = "Optional overline",
                        onValueChange = { nextOverline ->
                            if (canEdit) onChange(items.replaceAt(index, item.copy(overline = nextOverline)))
                        },
                    )
                }
                TextField(
                    theme = theme,
                    label = "Subtitle",
                    value = item.subtitle,
                    placeholder = "Optional subtitle",
                    onValueChange = { nextSubtitle ->
                        if (canEdit) onChange(items.replaceAt(index, item.copy(subtitle = nextSubtitle)))
                    },
                )
                ActionEditor(
                    action = item.action,
                    availablePages = availablePages,
                    canEdit = canEdit,
                    theme = theme,
                    onChange = { nextAction ->
                        onChange(items.replaceAt(index, item.copy(action = nextAction)))
                    },
                )
            }
        }

        ActionButton(
            label = "Add List Item",
            onClick = { if (canEdit) onChange(items + NativeUiListItemDraft(title = "New item")) },
            theme = theme,
            isDisabled = !canEdit,
            isPrimary = false,
        )
    }
}

@Composable
private fun ActionEditor(
    action: NativeUiActionDraft,
    availablePages: List<NativeUiPageDraft>,
    canEdit: Boolean,
    theme: MaterialThemePalette,
    onChange: (NativeUiActionDraft) -> Unit,
) {
    EditorPanel(
        title = "Action",
        description = "Pick where this tap should go.",
        theme = theme,
    ) {
        StringSelectField(
            theme = theme,
            label = "Action Type",
            value = action.type.name,
            options = NativeUiActionType.entries.map { it.name to it.label },
            isDisabled = !canEdit,
            onValueChange = { nextType ->
                if (canEdit) {
                    onChange(NativeUiActionDraft(type = NativeUiActionType.valueOf(nextType)))
                }
            },
        )

        when (action.type) {
            NativeUiActionType.None,
            NativeUiActionType.Refresh,
            -> Unit

            NativeUiActionType.Page -> {
                FieldRow {
                    StringSelectField(
                        theme = theme,
                        label = "Target Page",
                        value = action.pageId,
                        options = availablePages.map { page ->
                            page.id to (page.title.ifBlank { humanizeRouteId(page.id) } + " (${page.id})")
                        },
                        emptyLabel = "Select a page or use slug",
                        isDisabled = !canEdit,
                        onValueChange = { nextPageId ->
                            if (canEdit) onChange(action.copy(pageId = nextPageId))
                        },
                    )
                    TextField(
                        theme = theme,
                        label = "Target Slug",
                        value = action.slug,
                        placeholder = "optional-page-slug",
                        onValueChange = { nextSlug -> if (canEdit) onChange(action.copy(slug = nextSlug)) },
                    )
                }
            }

            NativeUiActionType.Url -> {
                TextField(
                    theme = theme,
                    label = "URL",
                    value = action.url,
                    placeholder = "https://example.com",
                    onValueChange = { nextUrl -> if (canEdit) onChange(action.copy(url = nextUrl)) },
                )
            }

            NativeUiActionType.Custom -> {
                FieldRow {
                    TextField(
                        theme = theme,
                        label = "Action ID",
                        value = action.actionId,
                        placeholder = "custom-action-id",
                        onValueChange = { nextActionId -> if (canEdit) onChange(action.copy(actionId = nextActionId)) },
                    )
                    TextField(
                        theme = theme,
                        label = "Payload",
                        value = action.payload,
                        placeholder = "optional-payload",
                        onValueChange = { nextPayload -> if (canEdit) onChange(action.copy(payload = nextPayload)) },
                    )
                }
            }
        }
    }
}

@Composable
private fun ColorRoleField(
    theme: MaterialThemePalette,
    label: String,
    value: NativeUiColorRoleDraft?,
    canEdit: Boolean,
    onValueChange: (NativeUiColorRoleDraft?) -> Unit,
) {
    StringSelectField(
        theme = theme,
        label = label,
        value = value?.wireValue.orEmpty(),
        options = NativeUiColorRoleDraft.entries.map { it.wireValue to it.label },
        emptyLabel = "Use default",
        isDisabled = !canEdit,
        onValueChange = { nextValue ->
            onValueChange(NativeUiColorRoleDraft.entries.firstOrNull { it.wireValue == nextValue })
        },
    )
}

@Composable
private fun EditorPanel(
    title: String,
    theme: MaterialThemePalette,
    description: String? = null,
    isEmphasized: Boolean = false,
    actions: (@Composable () -> Unit)? = null,
    content: @Composable () -> Unit,
) {
    Div(
        attrs = {
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(14.px)
                padding(18.px)
                borderRadius(22.px)
                property("background", theme.surfaceContainerHighest)
                property("border", "1px solid ${if (isEmphasized) theme.primary else theme.outlineVariant}")
                property(
                    "box-shadow",
                    if (isEmphasized) "0 10px 24px ${theme.shadow}, inset 0 1px 0 ${theme.scrim}" else "inset 0 1px 0 ${theme.scrim}"
                )
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    display(DisplayStyle.Flex)
                    property("justify-content", "space-between")
                    property("align-items", "center")
                    gap(12.px)
                    property("flex-wrap", "wrap")
                }
            }
        ) {
            Div(
                attrs = {
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(6.px)
                    }
                }
            ) {
                P(
                    attrs = {
                        style {
                            margin(0.px)
                            property("font-size", "0.82rem")
                            property("font-weight", "700")
                            property("letter-spacing", "0.08em")
                            property("text-transform", "uppercase")
                            property("color", theme.onSurfaceVariant)
                        }
                    }
                ) {
                    Text(title)
                }
                description?.let { body ->
                    P(
                        attrs = {
                            style {
                                margin(0.px)
                                property("font-size", "0.9rem")
                                property("color", theme.onSurfaceVariant)
                                property("line-height", "1.5")
                            }
                        }
                    ) {
                        Text(body)
                    }
                }
            }
            actions?.invoke()
        }
        content()
    }
}

@Composable
private fun PageChip(
    page: NativeUiPageDraft,
    isSelected: Boolean,
    isRoot: Boolean,
    requiredTabRouteIds: List<String>,
    theme: MaterialThemePalette,
    onClick: () -> Unit,
) {
    val label = page.title.ifBlank { humanizeRouteId(page.id) }
    val isRequiredRoot = page.id in requiredTabRouteIds
    Div(
        attrs = {
            style {
                padding(14.px, 16.px)
                borderRadius(18.px)
                property("background", theme.surfaceContainerHighest)
                property("color", theme.onSurface)
                property("border", "1px solid ${if (isSelected) theme.primary else theme.outlineVariant}")
                property("box-shadow", if (isSelected) "0 10px 24px ${theme.shadow}" else "inset 0 1px 0 ${theme.scrim}")
                property("min-width", "180px")
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(12.px)
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(6.px)
                }
            }
        ) {
            P(
                attrs = {
                    style {
                        margin(0.px)
                        property("font-size", "0.72rem")
                        property("font-weight", "700")
                        property("letter-spacing", "0.08em")
                        property("text-transform", "uppercase")
                        property("color", if (isSelected) theme.primary else theme.onSurfaceVariant)
                    }
                }
            ) {
                Text(
                    when {
                        isRoot -> "Root Page"
                        isRequiredRoot -> "Tab Page"
                        else -> "Subpage"
                    }
                )
            }
            P(
                attrs = {
                    style {
                        margin(0.px)
                        property("font-size", "1rem")
                        property("font-weight", "700")
                    }
                }
            ) {
                Text(label)
            }
            P(
                attrs = {
                    style {
                        margin(0.px)
                        property("font-size", "0.82rem")
                        property("color", theme.onSurfaceVariant)
                    }
                }
            ) {
                Text("${page.id} • ${page.blocks.size} blocks")
            }
        }
        ActionButton(
            label = if (isSelected) "Editing" else "Edit Page",
            onClick = onClick,
            theme = theme,
            isDisabled = isSelected,
            isPrimary = false,
        )
    }
}

@Composable
private fun StringSelectField(
    theme: MaterialThemePalette,
    label: String,
    value: String,
    options: List<Pair<String, String>>,
    emptyLabel: String = "Select",
    isDisabled: Boolean = false,
    onValueChange: (String) -> Unit,
) {
    Div(
        attrs = {
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(6.px)
                flex(1)
                property("min-width", "180px")
            }
        }
    ) {
        P(
            attrs = {
                style {
                    margin(0.px)
                    property("color", theme.onSurfaceVariant)
                    property("font-size", "0.78rem")
                    property("font-weight", "700")
                    property("letter-spacing", "0.06em")
                    property("text-transform", "uppercase")
                }
            }
        ) {
            Text(label)
        }
        Select(
            attrs = {
                onChange { onValueChange(it.value ?: "") }
                if (isDisabled) disabled()
                style {
                    padding(14.px, 16.px)
                    borderRadius(14.px)
                    property("box-sizing", "border-box")
                    property("width", "100%")
                    property("background-color", theme.surface)
                    property("color", theme.onSurface)
                    property("border", "1px solid ${theme.outline}")
                    property("box-shadow", "inset 0 1px 0 ${theme.scrim}")
                    property("font-size", "0.94rem")
                }
            }
        ) {
            Option(
                value = "",
                attrs = {
                    if (value.isBlank()) selected()
                }
            ) {
                Text(emptyLabel)
            }
            options.forEach { (optionValue, optionLabel) ->
                Option(
                    value = optionValue,
                    attrs = {
                        if (value == optionValue) selected()
                    }
                ) {
                    Text(optionLabel)
                }
            }
        }
    }
}

@Composable
private fun PreviewBlockNode(
    block: NativeUiBlockDraft,
    pathKey: String,
    selectedPath: String?,
    theme: MaterialThemePalette,
) {
    val isSelected = selectedPath == pathKey
    val hasSelectedDescendant = selectedPath?.startsWith("$pathKey/") == true

    Div(
        attrs = {
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(10.px)
                padding(12.px)
                borderRadius(14.px)
                property("background", if (isSelected) theme.primaryContainer else theme.surface)
                property("border", "1px solid ${if (isSelected || hasSelectedDescendant) theme.primary else theme.outlineVariant}")
            }
        }
    ) {
        P(
            attrs = {
                style {
                    margin(0.px)
                    property("font-size", "0.72rem")
                    property("font-weight", "700")
                    property("letter-spacing", "0.08em")
                    property("text-transform", "uppercase")
                    property("color", if (isSelected) theme.onPrimaryContainer else theme.onSurfaceVariant)
                }
            }
        ) {
            Text(block.type.label)
        }
        P(
            attrs = {
                style {
                    margin(0.px)
                    property("font-size", "0.95rem")
                    property("font-weight", "700")
                    property("color", if (isSelected) theme.onPrimaryContainer else theme.onSurface)
                }
            }
        ) {
            Text(blockDisplayTitle(block))
        }
        P(
            attrs = {
                style {
                    margin(0.px)
                    property("font-size", "0.84rem")
                    property("line-height", "1.5")
                    property("color", if (isSelected) theme.onPrimaryContainer else theme.onSurfaceVariant)
                }
            }
        ) {
            Text(blockMeta(block))
        }

        when (block) {
            is NativeUiSectionBlockDraft -> {
                block.blocks.forEachIndexed { index, child ->
                    PreviewBlockNode(
                        block = child,
                        pathKey = "$pathKey/block:$index",
                        selectedPath = selectedPath,
                        theme = theme,
                    )
                }
            }

            is NativeUiCardBlockDraft -> {
                block.blocks.forEachIndexed { index, child ->
                    PreviewBlockNode(
                        block = child,
                        pathKey = "$pathKey/block:$index",
                        selectedPath = selectedPath,
                        theme = theme,
                    )
                }
            }

            is NativeUiCarouselBlockDraft -> {
                block.items.forEachIndexed { index, item ->
                    PreviewLeafNode(
                        eyebrow = "Carousel Item",
                        title = item.title.ifBlank { "Item ${index + 1}" },
                        meta = item.subtitle.ifBlank { item.imageUrl.ifBlank { "No image URL yet." } },
                        pathKey = "$pathKey/item:$index",
                        selectedPath = selectedPath,
                        theme = theme,
                    )
                }
            }

            is NativeUiListBlockDraft -> {
                block.items.forEachIndexed { index, item ->
                    PreviewLeafNode(
                        eyebrow = "List Item",
                        title = item.title.ifBlank { "Item ${index + 1}" },
                        meta = item.subtitle.ifBlank { item.overline.ifBlank { "No subtitle yet." } },
                        pathKey = "$pathKey/item:$index",
                        selectedPath = selectedPath,
                        theme = theme,
                    )
                }
            }

            else -> Unit
        }
    }
}

@Composable
private fun PreviewLeafNode(
    eyebrow: String,
    title: String,
    meta: String,
    pathKey: String,
    selectedPath: String?,
    theme: MaterialThemePalette,
) {
    val isSelected = selectedPath == pathKey

    Div(
        attrs = {
            style {
                padding(12.px)
                borderRadius(12.px)
                property("background", if (isSelected) theme.primaryContainer else theme.surfaceContainerHighest)
                property("border", "1px solid ${if (isSelected) theme.primary else theme.outlineVariant}")
            }
        }
    ) {
        P(
            attrs = {
                style {
                    margin(0.px)
                    property("font-size", "0.72rem")
                    property("font-weight", "700")
                    property("letter-spacing", "0.08em")
                    property("text-transform", "uppercase")
                    property("color", if (isSelected) theme.onPrimaryContainer else theme.onSurfaceVariant)
                }
            }
        ) {
            Text(eyebrow)
        }
        P(
            attrs = {
                style {
                    property("margin-top", "4px")
                    margin(0.px)
                    property("font-size", "0.92rem")
                    property("font-weight", "700")
                    property("color", if (isSelected) theme.onPrimaryContainer else theme.onSurface)
                }
            }
        ) {
            Text(title)
        }
        P(
            attrs = {
                style {
                    property("margin-top", "4px")
                    margin(0.px)
                    property("font-size", "0.82rem")
                    property("line-height", "1.45")
                    property("color", if (isSelected) theme.onPrimaryContainer else theme.onSurfaceVariant)
                }
            }
        ) {
            Text(meta)
        }
    }
}

private fun buildEditorOutlineEntries(page: NativeUiPageDraft): List<EditorOutlineEntry> {
    val pageLabel = page.title.ifBlank { humanizeRouteId(page.id) }
    return buildList {
        page.blocks.forEachIndexed { index, block ->
            addBlockOutlineEntry(
                pathKey = "page:${page.id}/block:$index",
                depth = 0,
                breadcrumb = "$pageLabel / ${block.type.label} ${index + 1}",
                block = block,
            )
        }
    }
}

private fun MutableList<EditorOutlineEntry>.addBlockOutlineEntry(
    pathKey: String,
    depth: Int,
    breadcrumb: String,
    block: NativeUiBlockDraft,
) {
    add(
        EditorOutlineEntry(
            pathKey = pathKey,
            depth = depth,
            eyebrow = block.type.label,
            title = blockDisplayTitle(block),
            meta = blockMeta(block),
            breadcrumb = breadcrumb,
        )
    )

    when (block) {
        is NativeUiSectionBlockDraft -> {
            block.blocks.forEachIndexed { index, child ->
                addBlockOutlineEntry(
                    pathKey = "$pathKey/block:$index",
                    depth = depth + 1,
                    breadcrumb = "$breadcrumb / ${child.type.label} ${index + 1}",
                    block = child,
                )
            }
        }

        is NativeUiCardBlockDraft -> {
            block.blocks.forEachIndexed { index, child ->
                addBlockOutlineEntry(
                    pathKey = "$pathKey/block:$index",
                    depth = depth + 1,
                    breadcrumb = "$breadcrumb / ${child.type.label} ${index + 1}",
                    block = child,
                )
            }
        }

        is NativeUiCarouselBlockDraft -> {
            block.items.forEachIndexed { index, item ->
                add(
                    EditorOutlineEntry(
                        pathKey = "$pathKey/item:$index",
                        depth = depth + 1,
                        eyebrow = "Carousel Item",
                        title = item.title.ifBlank { "Item ${index + 1}" },
                        meta = item.subtitle.ifBlank { item.imageUrl.ifBlank { "No image URL yet." } },
                        breadcrumb = "$breadcrumb / Carousel Item ${index + 1}",
                    )
                )
            }
        }

        is NativeUiListBlockDraft -> {
            block.items.forEachIndexed { index, item ->
                add(
                    EditorOutlineEntry(
                        pathKey = "$pathKey/item:$index",
                        depth = depth + 1,
                        eyebrow = "List Item",
                        title = item.title.ifBlank { "Item ${index + 1}" },
                        meta = item.subtitle.ifBlank { item.overline.ifBlank { "No subtitle yet." } },
                        breadcrumb = "$breadcrumb / List Item ${index + 1}",
                    )
                )
            }
        }

        else -> Unit
    }
}

private fun nativeUiPageKindLabel(
    page: NativeUiPageDraft,
    rootPageId: String,
    requiredTabRouteIds: List<String>,
): String =
    when {
        page.id == rootPageId -> "Root page"
        page.id in requiredTabRouteIds -> "Tab page"
        else -> "Subpage"
    }

private fun blockDisplayTitle(block: NativeUiBlockDraft): String =
    when (block) {
        is NativeUiTextBlockDraft -> block.text.ifBlank { "Text Block" }
        is NativeUiImageBlockDraft -> block.contentDescription.ifBlank { block.url.ifBlank { "Image Block" } }
        is NativeUiButtonBlockDraft -> block.label.ifBlank { "Button Block" }
        is NativeUiSpacerBlockDraft -> "Spacer"
        is NativeUiSectionBlockDraft -> block.title.ifBlank { "Section" }
        is NativeUiCardBlockDraft -> block.title.ifBlank { "Card" }
        is NativeUiCarouselBlockDraft -> "Carousel"
        is NativeUiListBlockDraft -> "List"
        is NativeUiEventCardBlockDraft -> block.title.ifBlank { "Event Card" }
        is NativeUiTicketCardBlockDraft -> block.title.ifBlank { "Ticket Card" }
    }

private fun blockMeta(block: NativeUiBlockDraft): String =
    when (block) {
        is NativeUiTextBlockDraft -> "${block.variant.label} text."
        is NativeUiImageBlockDraft -> if (block.url.isBlank()) "No image URL yet." else block.url
        is NativeUiButtonBlockDraft -> if (block.action.type == NativeUiActionType.None) "No action assigned yet." else "Action: ${block.action.type.label}."
        is NativeUiSpacerBlockDraft -> "${block.heightDp}dp spacing."
        is NativeUiSectionBlockDraft -> "${block.blocks.size} nested block${if (block.blocks.size == 1) "" else "s"}."
        is NativeUiCardBlockDraft -> "${block.blocks.size} nested block${if (block.blocks.size == 1) "" else "s"} plus card content."
        is NativeUiCarouselBlockDraft -> "${block.items.size} carousel item${if (block.items.size == 1) "" else "s"}."
        is NativeUiListBlockDraft -> "${block.items.size} list item${if (block.items.size == 1) "" else "s"}."
        is NativeUiEventCardBlockDraft -> block.subtitle.ifBlank { "Event card content." }
        is NativeUiTicketCardBlockDraft -> block.status.ifBlank { "Ticket card content." }
    }

private fun updateBlockId(block: NativeUiBlockDraft, nextId: String): NativeUiBlockDraft =
    when (block) {
        is NativeUiTextBlockDraft -> block.copy(id = nextId)
        is NativeUiImageBlockDraft -> block.copy(id = nextId)
        is NativeUiButtonBlockDraft -> block.copy(id = nextId)
        is NativeUiSpacerBlockDraft -> block.copy(id = nextId)
        is NativeUiSectionBlockDraft -> block.copy(id = nextId)
        is NativeUiCardBlockDraft -> block.copy(id = nextId)
        is NativeUiCarouselBlockDraft -> block.copy(id = nextId)
        is NativeUiListBlockDraft -> block.copy(id = nextId)
        is NativeUiEventCardBlockDraft -> block.copy(id = nextId)
        is NativeUiTicketCardBlockDraft -> block.copy(id = nextId)
    }

private fun defaultBlockDraft(type: NativeUiBlockType): NativeUiBlockDraft =
    when (type) {
        NativeUiBlockType.Text -> NativeUiTextBlockDraft(text = "New text block")
        NativeUiBlockType.Image -> NativeUiImageBlockDraft(url = "https://example.com/image.png")
        NativeUiBlockType.Button -> NativeUiButtonBlockDraft(label = "New Button")
        NativeUiBlockType.Spacer -> NativeUiSpacerBlockDraft()
        NativeUiBlockType.Section -> NativeUiSectionBlockDraft(title = "Section Title")
        NativeUiBlockType.Card -> NativeUiCardBlockDraft(title = "Card Title")
        NativeUiBlockType.Carousel -> NativeUiCarouselBlockDraft(
            items = listOf(
                NativeUiCarouselItemDraft(
                    imageUrl = "https://example.com/highlight.jpg",
                    title = "New highlight",
                )
            )
        )
        NativeUiBlockType.List -> NativeUiListBlockDraft(items = listOf(NativeUiListItemDraft(title = "New item")))
        NativeUiBlockType.EventCard -> NativeUiEventCardBlockDraft(title = "Event Title")
        NativeUiBlockType.TicketCard -> NativeUiTicketCardBlockDraft(title = "Ticket Title")
    }

private fun nextAvailablePageId(pages: List<NativeUiPageDraft>): String {
    val existingIds = pages.map { it.id }.toSet()
    var index = pages.size + 1
    var candidate = "page-$index"
    while (candidate in existingIds) {
        index += 1
        candidate = "page-$index"
    }
    return candidate
}

private fun humanizeRouteId(routeId: String): String =
    routeId
        .split('-', '_')
        .filter { it.isNotBlank() }
        .joinToString(" ") { token ->
            token.replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() }
        }
        .ifBlank { "Untitled Page" }

private fun ensureOnlyKeys(value: JsonObject, allowedKeys: Set<String>, path: String) {
    val unsupportedKeys = value.keys - allowedKeys
    if (unsupportedKeys.isNotEmpty()) {
        error("$path uses unsupported field(s): ${unsupportedKeys.joinToString(", ")}. Use the advanced JSON editor for this document.")
    }
}

private fun JsonObject.requiredString(key: String, path: String): String =
    this[key]?.jsonPrimitive?.contentOrNull
        ?: error("$path is missing required \"$key\".")

private fun JsonObject.optionalString(key: String): String =
    this[key]?.jsonPrimitive?.contentOrNull.orEmpty()

private fun JsonObject.requiredArray(key: String, path: String): List<JsonElement> =
    this[key]?.jsonArray ?: error("$path is missing required \"$key\".")

private fun <T> parseEnum(
    value: String,
    options: List<T>,
    path: String,
): T where T : Enum<T> {
    return options.firstOrNull { option -> option.name.equals(value, ignoreCase = true) }
        ?: error("$path uses unsupported value \"$value\".")
}

private fun <T> JsonObject.optionalEnum(
    key: String,
    options: List<T>,
    path: String,
): T? where T : Enum<T> {
    val value = this[key]?.jsonPrimitive?.contentOrNull ?: return null
    return parseEnum(value = value, options = options, path = path)
}

private fun String.ifNotBlank(block: (String) -> Unit) {
    if (isNotBlank()) block(this)
}

private fun <T> List<T>.replaceAt(index: Int, value: T): List<T> =
    mapIndexed { currentIndex, currentValue ->
        if (currentIndex == index) value else currentValue
    }

private fun <T> List<T>.move(fromIndex: Int, toIndex: Int): List<T> {
    if (fromIndex !in indices || toIndex !in indices || fromIndex == toIndex) return this

    return toMutableList().apply {
        add(toIndex, removeAt(fromIndex))
    }
}

private fun <T> List<T>.removeAt(index: Int): List<T> =
    filterIndexed { currentIndex, _ -> currentIndex != index }
